import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import AppLayout from "@/components/AppLayout";
import { useAuth } from "@/contexts/AuthContext";
import { Plus, MapPin, Calendar, Users, ArrowUpRight, Trash2, Copy, BookMarked } from "lucide-react";

const formatMoney = (n, c = "CHF") => {
    const sym = { CHF: "CHF", EUR: "€" }[c] || c;
    return `${sym} ${Number(n || 0).toLocaleString("de-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatDate = (s) => {
    if (!s) return "—";
    try { return new Date(s).toLocaleDateString("de-CH", { day: "2-digit", month: "short", year: "numeric" }); } catch { return s; }
};

export default function DashboardPage() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [trips, setTrips] = useState([]);
    const [excursionCount, setExcursionCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [showNew, setShowNew] = useState(false);
    const [creating, setCreating] = useState(false);
    const [form, setForm] = useState({
        title: "",
        client_name: "",
        destination: "Hurghada",
        start_date: "",
        end_date: "",
        num_adults: 2,
        children_ages: [],
        currency: "CHF",
    });

    const load = async () => {
        setLoading(true);
        try {
            const [t, e] = await Promise.all([api.get("/trips"), api.get("/excursions")]);
            setTrips(t.data);
            setExcursionCount(e.data.length);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const handleCreate = async (e) => {
        e.preventDefault();
        setCreating(true);
        try {
            const { data } = await api.post("/trips", form);
            navigate(`/trips/${data.id}`);
        } catch (ex) {
            alert(formatApiError(ex));
        } finally {
            setCreating(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Diese Reise unwiderruflich löschen?")) return;
        await api.delete(`/trips/${id}`);
        load();
    };

    const handleDuplicate = async (id) => {
        await api.post(`/trips/${id}/duplicate`);
        load();
    };

    const totalValue = trips.reduce((s, t) => s + (t.total_cost || 0), 0);
    const draftCount = trips.filter((t) => t.status === "draft").length;

    return (
        <AppLayout>
            <div className="flex items-end justify-between mb-12">
                <div>
                    <p className="pt-eyebrow mb-3">Dashboard</p>
                    <h1 className="pt-h1" data-testid="dashboard-heading">
                        Ahlan, <span className="italic font-normal" style={{ fontFamily: "Fraunces" }}>{user?.first_name || user?.name?.split(" ")[0] || "Berater"}</span>.
                    </h1>
                    <p className="pt-body mt-3 max-w-2xl">
                        {trips.length === 0
                            ? "Lege deine erste massgeschneiderte Ägypten-Reise an. Die Content-Datenbank ist mit 8 vorbereiteten Ausflügen für Hurghada und Marsa Alam bestückt."
                            : `Du hast ${trips.length} Reise${trips.length === 1 ? "" : "n"} im Atelier. Zeit, etwas Neues zu komponieren.`}
                    </p>
                </div>
                <button
                    type="button"
                    onClick={() => setShowNew(true)}
                    data-testid="new-trip-button"
                    className="group bg-terracotta text-white hover:bg-terracotta-hover rounded-md px-5 py-3 font-medium transition-all duration-200 flex items-center gap-2 shadow-card hover:shadow-hover whitespace-nowrap"
                >
                    <Plus className="w-4 h-4" />
                    Neue Reise
                </button>
            </div>

            {/* KPIs */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-14" data-testid="kpi-row">
                <KpiCard label="Aktive Reisen" value={trips.length} accent="navy" testid="kpi-trips" />
                <KpiCard label="Gesamtvolumen" value={formatMoney(totalValue, "CHF")} accent="terracotta" testid="kpi-volume" />
                <KpiCard label="Content-DB · Ausflüge" value={excursionCount} accent="ink" testid="kpi-excursions" />
            </div>

            {/* Trips list */}
            <div className="flex items-center justify-between mb-5">
                <h2 className="pt-h3">Deine Reisen</h2>
                {draftCount > 0 && (
                    <span className="text-xs text-ink-secondary"><span className="font-semibold text-ink">{draftCount}</span> Entwürfe</span>
                )}
            </div>

            {loading ? (
                <div className="bg-white border border-sand-300 rounded-lg p-12 text-center text-ink-secondary">Lade Reisen…</div>
            ) : trips.length === 0 ? (
                <div className="bg-white border border-sand-300 rounded-lg p-12 text-center" data-testid="empty-trips">
                    <MapPin className="w-10 h-10 text-sand-400 mx-auto mb-4" strokeWidth={1.5} />
                    <p className="pt-h3 mb-2">Noch keine Reisen</p>
                    <p className="pt-body mb-5">Klicke auf "Neue Reise", um deine erste Reise anzulegen.</p>
                    <Link to="/content" className="text-sm text-terracotta hover:text-terracotta-hover font-semibold inline-flex items-center gap-1">
                        <BookMarked className="w-4 h-4" /> Erst Content-DB ansehen
                    </Link>
                </div>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="trips-list">
                    {trips.map((t) => (
                        <div key={t.id} data-testid={`trip-card-${t.id}`} className="pt-card-interactive group relative">
                            <span className="pt-accent-bar opacity-0 group-hover:opacity-100 transition-opacity" />
                            <div className="p-6">
                                <div className="flex items-start justify-between gap-4 mb-4">
                                    <div className="flex-1 min-w-0">
                                        <p className="pt-eyebrow mb-2">{t.destination}</p>
                                        <Link to={`/trips/${t.id}`} className="block">
                                            <h3 className="pt-h3 mb-1 group-hover:text-terracotta transition-colors truncate">{t.title || "Unbenannte Reise"}</h3>
                                        </Link>
                                        <p className="text-sm text-ink-secondary truncate">Kunde: <span className="font-medium text-ink">{t.client_name || "—"}</span></p>
                                    </div>
                                    <span className={`text-[10px] uppercase tracking-[0.14em] font-bold px-2 py-1 rounded ${
                                        t.status === "confirmed" ? "bg-status-success/15 text-status-success" :
                                        t.status === "sent" ? "bg-status-info/15 text-status-info" :
                                        "bg-sand-deep text-ink-secondary"
                                    }`}>{t.status}</span>
                                </div>
                                <div className="grid grid-cols-3 gap-3 mb-4 text-sm">
                                    <div>
                                        <p className="text-[10px] uppercase tracking-[0.14em] text-ink-tertiary mb-1">Reise</p>
                                        <p className="flex items-center gap-1.5 text-ink"><Calendar className="w-3.5 h-3.5" /> {formatDate(t.start_date)}</p>
                                    </div>
                                    <div>
                                        <p className="text-[10px] uppercase tracking-[0.14em] text-ink-tertiary mb-1">Pers.</p>
                                        <p className="flex items-center gap-1.5 text-ink"><Users className="w-3.5 h-3.5" /> {(t.num_adults ?? t.num_travelers ?? 0)}{(t.children_ages?.length ? ` + ${t.children_ages.length} Kind${t.children_ages.length === 1 ? "" : "er"}` : "")}</p>
                                    </div>
                                    <div>
                                        <p className="text-[10px] uppercase tracking-[0.14em] text-ink-tertiary mb-1">Total</p>
                                        <p className="font-semibold text-ink">{formatMoney(t.total_cost || 0, t.currency)}</p>
                                    </div>
                                </div>
                                <div className="flex items-center justify-between pt-4 border-t border-sand-300">
                                    <Link to={`/trips/${t.id}`} data-testid={`open-trip-${t.id}`} className="text-sm font-semibold text-navy hover:text-terracotta inline-flex items-center gap-1.5 transition-colors">
                                        Bearbeiten <ArrowUpRight className="w-3.5 h-3.5" />
                                    </Link>
                                    <div className="flex items-center gap-1">
                                        <button onClick={() => handleDuplicate(t.id)} title="Duplizieren" data-testid={`duplicate-trip-${t.id}`} className="p-2 rounded hover:bg-sand-deep text-ink-secondary hover:text-navy transition-colors">
                                            <Copy className="w-4 h-4" />
                                        </button>
                                        <button onClick={() => handleDelete(t.id)} title="Löschen" data-testid={`delete-trip-${t.id}`} className="p-2 rounded hover:bg-terracotta/10 text-ink-secondary hover:text-terracotta transition-colors">
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* New Trip Modal */}
            {showNew && (
                <div className="fixed inset-0 bg-navy/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-hover w-full max-w-lg relative overflow-hidden max-h-[92vh] overflow-y-auto" data-testid="new-trip-modal">
                        <span className="pt-accent-bar" />
                        <form onSubmit={handleCreate} className="p-8">
                            <p className="pt-eyebrow mb-2">Neue Reise</p>
                            <h2 className="pt-h2 mb-6">Stammdaten</h2>
                            <div className="space-y-4">
                                <Field label="Reisetitel" required value={form.title} onChange={(v) => setForm({ ...form, title: v })} testid="new-trip-title" placeholder="z.B. Honeymoon Hurghada Mai 2026" />
                                <Field label="Kunde" value={form.client_name} onChange={(v) => setForm({ ...form, client_name: v })} testid="new-trip-client" placeholder="Familie Müller" />
                                <div className="grid grid-cols-2 gap-3">
                                    <SelectField label="Zielort" value={form.destination} onChange={(v) => setForm({ ...form, destination: v })} options={["Hurghada", "Marsa Alam", "Sharm el-Sheikh", "Kairo", "Luxor"]} testid="new-trip-destination" />
                                    <SelectField label="Währung" value={form.currency} onChange={(v) => setForm({ ...form, currency: v })} options={["CHF", "EUR"]} testid="new-trip-currency" />
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <Field type="date" label="Anreise" value={form.start_date} onChange={(v) => setForm({ ...form, start_date: v })} testid="new-trip-start" />
                                    <Field type="date" label="Abreise" value={form.end_date} onChange={(v) => setForm({ ...form, end_date: v })} testid="new-trip-end" />
                                </div>
                                <Field type="number" label="Erwachsene" value={form.num_adults} onChange={(v) => {
                                    const n = parseInt(v);
                                    setForm({ ...form, num_adults: Number.isFinite(n) ? Math.max(1, Math.min(20, n)) : 1 });
                                }} testid="new-trip-adults" />
                                <ChildrenPicker
                                    ages={form.children_ages}
                                    onChange={(ages) => setForm({ ...form, children_ages: ages })}
                                />
                            </div>
                            <div className="flex justify-end gap-3 mt-7">
                                <button type="button" onClick={() => setShowNew(false)} data-testid="new-trip-cancel" className="px-5 py-2.5 rounded-md border border-sand-300 text-ink-secondary hover:bg-sand-deep transition-colors">Abbrechen</button>
                                <button type="submit" disabled={creating} data-testid="new-trip-submit" className="px-5 py-2.5 rounded-md bg-navy text-sand hover:bg-navy-hover transition-colors font-medium disabled:opacity-60">
                                    {creating ? "Erstelle…" : "Reise anlegen"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </AppLayout>
    );
}

function KpiCard({ label, value, accent, testid }) {
    const accentColor = { navy: "border-l-navy", terracotta: "border-l-terracotta", ink: "border-l-ink" }[accent];
    return (
        <div className={`bg-white border border-sand-300 border-l-4 ${accentColor} rounded-lg p-6`} data-testid={testid}>
            <p className="pt-eyebrow mb-2">{label}</p>
            <p className="text-3xl font-bold text-ink" style={{ fontFamily: "Space Grotesk" }}>{value}</p>
        </div>
    );
}

function Field({ label, value, onChange, type = "text", required = false, placeholder = "", testid }) {
    return (
        <div>
            <label className="block text-xs font-semibold uppercase tracking-[0.14em] text-ink-secondary mb-2">{label}</label>
            <input type={type} required={required} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} placeholder={placeholder}
                className="w-full bg-white border border-sand-300 rounded-md px-4 py-2.5 text-ink focus:outline-none focus:ring-2 focus:ring-navy" />
        </div>
    );
}

function SelectField({ label, value, onChange, options, testid }) {
    return (
        <div>
            <label className="block text-xs font-semibold uppercase tracking-[0.14em] text-ink-secondary mb-2">{label}</label>
            <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
                className="w-full bg-white border border-sand-300 rounded-md px-4 py-2.5 text-ink focus:outline-none focus:ring-2 focus:ring-navy">
                {options.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
        </div>
    );
}

function ChildrenPicker({ ages, onChange }) {
    const list = Array.isArray(ages) ? ages : [];
    const count = list.length;
    const setCount = (n) => {
        const next = Math.max(0, Math.min(5, n));
        if (next === count) return;
        if (next > count) {
            onChange([...list, ...Array(next - count).fill(0)]);
        } else {
            onChange(list.slice(0, next));
        }
    };
    const setAge = (idx, val) => {
        const n = parseInt(val);
        const safe = Number.isFinite(n) ? Math.max(0, Math.min(17, n)) : 0;
        const next = [...list];
        next[idx] = safe;
        onChange(next);
    };
    return (
        <div>
            <label className="block text-xs font-semibold uppercase tracking-[0.14em] text-ink-secondary mb-2">Kinder (max. 5)</label>
            <div className="flex items-center gap-2">
                <button
                    type="button"
                    onClick={() => setCount(count - 1)}
                    disabled={count === 0}
                    data-testid="new-trip-children-minus"
                    className="w-9 h-9 rounded-md border border-sand-300 text-ink hover:bg-sand-deep disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
                    aria-label="Weniger Kinder"
                >−</button>
                <div className="px-4 py-2 rounded-md border border-sand-300 bg-white min-w-[3rem] text-center font-semibold text-ink" data-testid="new-trip-children-count">
                    {count}
                </div>
                <button
                    type="button"
                    onClick={() => setCount(count + 1)}
                    disabled={count >= 5}
                    data-testid="new-trip-children-plus"
                    className="w-9 h-9 rounded-md border border-sand-300 text-ink hover:bg-sand-deep disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
                    aria-label="Mehr Kinder"
                >+</button>
                <span className="ml-2 text-xs text-ink-tertiary">Kind{count === 1 ? "" : "er"}</span>
            </div>
            {count > 0 && (
                <div className="mt-3 grid grid-cols-5 gap-2">
                    {list.map((age, i) => (
                        <div key={i}>
                            <label className="block text-[10px] font-semibold uppercase tracking-[0.14em] text-ink-tertiary mb-1">Kind {i + 1} (Alter)</label>
                            <input
                                type="number"
                                min="0"
                                max="17"
                                value={Number.isFinite(age) ? age : 0}
                                onChange={(e) => setAge(i, e.target.value)}
                                data-testid={`new-trip-child-age-${i}`}
                                className="w-full bg-white border border-sand-300 rounded-md px-2.5 py-2 text-ink focus:outline-none focus:ring-2 focus:ring-navy text-sm"
                            />
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
